import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Step,
  StepLabel,
  StepLabelProps,
  Stepper,
  styled,
  Divider,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { useForm } from '@/utilities'
import { ApproveModal } from './ApproveModal'
import { useMaintenanceTicketsContext } from '@/context/MaintenanceTicketsContext'
import { DenyModal } from './DenyModal'
import { TicketData } from '../view-ticket/TicketData'
import { QuotationForm } from '../view-ticket/quotation/QuotationForm'
import { PurchaseRequestForm } from '../view-ticket/purchase-request/PurchaseRequestForm'
import { PurchaseOrderForm } from '../view-ticket/purchase-order/PurchaseOrderForm'
import {
  handleIsNextAvailableWithRole,
  handleIsStepAvailable,
  handleValidateIsAwardedExist,
  handleValidatePurchaseOrderData,
  handleValidatePurchaseRequestData,
  handleValidateQuotationData,
} from '@/constants'
import { generatePdfById } from '@/api/maintenance-tickets-api'
import { OnHoldTicketModal } from './OnHoldTicketModal'
import { ApproveDISModal } from './ApproveDISModal'
import { ApproveDISWithGatePassModal } from './ApproveDISWithGatePassModal'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

interface ActionButtonProps extends ButtonProps {
  bgcolor: string
}
interface ViewTicketControllerModalProps {
  title: string
  open: boolean
  role: string
  onClose: () => void
  data: any | null
  tab: string
}

type Attachment = {
  id: number
  fileName: string
  presignedUrl: string
  fileType: string
  attachmentType: string
}

type Quotation = {
  contractorName: string
  amount: string | number
  remark: string
  isAwarded: boolean
  attachments: Attachment[]
  dateCreated: string
  contractorId?: string | number
}

type PurchaseRequest = {
  purchaseRequestNumber: string | number
  attachments: Attachment[]
}

interface PurchaseOrder {
  purchaseOrderDtos: {
    attachments: Attachment[]
    retainAttachmentIds?: number[]
    purchaseOrderNumber: string
  }[]
  purchaseRequest: {
    attachments: Attachment[]
    retainAttachmentIds?: number[]
    purchaseRequestNumber: string
  }
}

const ActionButton = styled(Button)<ActionButtonProps>(({ bgcolor }) => ({
  backgroundColor: bgcolor,
}))

interface CustomStepLabelProps extends StepLabelProps {
  isHighlighted?: boolean
}

const StyledStepLabel = styled(StepLabel)<CustomStepLabelProps>(
  ({ theme, isHighlighted }) => ({
    cursor: 'pointer',
    '&:hover .MuiStepLabel-label': {
      textDecoration: 'underline',
    },
    '& .MuiStepLabel-label': {
      fontFamily: 'Gabarito',
      fontWeight: 600,
      fontSize: 14,
      transform: 'translateY(-10px)',
      color: isHighlighted ? '#495AFF' : undefined,
      [theme.breakpoints.down('sm')]: {
        fontSize: 12,
      },
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      color: isHighlighted ? '#495AFF' : undefined,
      [theme.breakpoints.down('sm')]: {
        width: 15,
        height: 15,
      },
    },
    '& .MuiStepIcon-text': {
      fontSize: 14,
      fontWeight: 500,
      [theme.breakpoints.down('sm')]: {
        fontSize: 12,
      },
    },
  }),
)

const ViewTicketModal = ({
  open,
  onClose,
  data,
  title,
  role,
  tab,
}: ViewTicketControllerModalProps) => {
  const {
    dispatch,
    mutateForProcessing,
    contractorQuotation,
    mutateContractorQuotation,
    purchaseRequest,
    mutatePurchaseRequest,
    mutatePurchaseOrder,
    purchaseOrder,
    actions: {
      approveMaintenanceTicket,
      denyMaintenanceTicket,
      reviseMaintenanceTicket,
      saveContractorQuotation,
      savePurchaseRequest,
      savePurchaseOrder,
      recallTicket,
      onHoldTicket,
      approveCoCMaintenanceTicket,
    },
  } = useMaintenanceTicketsContext()

  const [activeStep, setActiveStep] = useState(1)
  const [quotationData, setQuotationData] = useState<Quotation[]>(
    contractorQuotation ?? [],
  )

  const canAct = role === data.assignedTo

  const [purchaseRequestData, setPurchaseRequestData] = useState<
    PurchaseRequest[]
  >(purchaseRequest ?? [])

  const [purchaseOrderData, setPurchaseOrderData] = useState<PurchaseOrder[]>(
    purchaseOrder ?? [],
  )

  const [isApproving, setIsApproving] = useState(false)
  const [isDenying, setIsDenying] = useState(false)
  const [isOnHolding, setIsOnHolding] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isRecalling, setIsRecalling] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  useEffect(() => {
    if (contractorQuotation && contractorQuotation.length > 0) {
      setQuotationData(contractorQuotation)
    }
  }, [contractorQuotation])

  useEffect(() => {
    if (purchaseRequest && purchaseRequest.length > 0) {
      setPurchaseRequestData(purchaseRequest)
    }
  }, [purchaseRequest])

  useEffect(() => {
    if (purchaseOrder && purchaseOrder.length > 0) {
      setPurchaseOrderData(purchaseOrder)
    }
  }, [purchaseOrder])

  const {
    formData: approveData,
    showModal: showApprove,
    handleUpdateData: handleShowApprove,
    handleCloseModal: handleCloseApprove,
  } = useForm()

  const {
    formData: onHoldData,
    showModal: showOnHold,
    handleUpdateData: handleShowOnHold,
    handleCloseModal: handleCloseOnHold,
  } = useForm()

  const {
    formData: denyData,
    showModal: showDeny,
    handleUpdateData: handleShowDeny,
    handleCloseModal: handleCloseDeny,
  } = useForm()

  const {
    formData: approveDISData,
    showModal: showApproveDIS,
    handleUpdateData: handleShowApproveDIS,
    handleCloseModal: handleCloseApproveDIS,
  } = useForm()

  const {
    formData: approveDISWithGatePassData,
    showModal: showApproveDISWithGatePass,
    handleUpdateData: handleShowApproveDISWithGatePass,
    handleCloseModal: handleCloseApproveDISWithGatePass,
  } = useForm()

  const handleDenyTicket = async (ticketId: number, note: string) => {
    setIsDenying(true)
    try {
    let data = {
      ticketId: ticketId,
      note: note,
    }
    await denyMaintenanceTicket(data)(dispatch)
    mutateForProcessing()
    handleCloseDeny()
    onClose()
    } finally {
      setIsDenying(false)
    }
  }

  const handleReviseTicket = async (
    ticketId: number,
    note: string,
    sourcerUserId: number,
  ) => {
    setIsDenying(true)
    try {
    let data = {
      ticketId: ticketId,
      note: note,
      sourceUserId: sourcerUserId,
    }

    await reviseMaintenanceTicket(data)(dispatch)
    mutateForProcessing()
    handleCloseDeny()
    onClose()
    } finally {
      setIsDenying(false)
    }
  }

  const handleApproveTicket = async (
    ticketId: number,
    note: string,
    cocDate?: any,
    projectDate?: any,
    shouldShowUpdate?: any,
  ) => {
    setIsApproving(true)
    try {
    dayjs.extend(utc)

    let cocData = {
      dateCocReceived:
        cocDate &&
        typeof cocDate === 'object' &&
        typeof cocDate.toDate === 'function'
          ? dayjs(cocDate).utc(true).toISOString()
          : null,
      dateCompletion:
        projectDate &&
        typeof projectDate === 'object' &&
        typeof projectDate.toDate === 'function'
          ? dayjs(projectDate).utc(true).toISOString()
          : null,
    }

    let data = {
      ticketId: ticketId,
      note: note,
      dateCocReceived:
        cocDate &&
        typeof cocDate === 'object' &&
        typeof cocDate.toDate === 'function'
          ? dayjs(cocDate).utc(true).toISOString()
          : null,
      dateCompletion:
        projectDate &&
        typeof projectDate === 'object' &&
        typeof projectDate.toDate === 'function'
          ? dayjs(projectDate).utc(true).toISOString()
          : null,
    }

    if (shouldShowUpdate) {
      await approveCoCMaintenanceTicket(ticketId, cocData)(dispatch)
    } else {
      await approveMaintenanceTicket(data)(dispatch)
    }

    handleCloseApprove()
    handleCloseApproveDIS()
    handleCloseApproveDISWithGatePass()
    onClose()
    mutateForProcessing()
    } finally {
      setIsApproving(false)
    }
  }

  const handleSave = async (isApprove: boolean) => {
    isApprove ? setIsSubmitting(true) : setIsSaving(true)
    try {
    if (
      data.currentLayerId === 4 &&
      data.currentTicketHolder === 'GA Purchasing Staff'
    ) {
      if (!handleValidateQuotationData(quotationData)) {
      }
      const payload = quotationData.map((data) => ({
        contractorId: data.contractorId ?? 0,
        contractorName: data.contractorName || 'string',
        isAwarded: data.isAwarded,
        amount: data.amount || 0,
        remarks: data.remark || '',
        retainAttachmentIds: data.attachments.map(
          (attachment) => attachment.id,
        ),
      }))
      const retainContractorNames = quotationData.map(
        (data) => data.contractorName,
      )

      const formDataPayload = {
        ticketId: data.id,
        retainContractorNames,
        isApprove,
        payload,
      }
      await saveContractorQuotation(formDataPayload)(dispatch)
      if (isApprove) {
        onClose()
      } else {
        mutateContractorQuotation()
      }
    } else if (
      (data.currentLayerId === 15 || data.currentLayerId === 14) &&
      data.currentTicketHolder === 'DND Purchasing Admin'
    ) {
      const payload = purchaseRequestData.map((data) => ({
        purchaseRequestNumber: data.purchaseRequestNumber || 'string',
        retainAttachmentIds: data.attachments.map(
          (attachment) => attachment.id,
        ),
      }))
      const retainPurchaseRequestNumbers = purchaseRequestData.map(
        (data) => data.purchaseRequestNumber,
      )

      const formDataPayload = {
        ticketId: data.id,
        retainPurchaseRequestNumbers,
        isApprove,
        payload,
      }
      await savePurchaseRequest(formDataPayload)(dispatch)
      if (isApprove) {
        onClose()
      } else {
        mutatePurchaseRequest()
      }
    } else if (
      (data.currentLayerId === 15 || data.currentLayerId === 16) &&
      data.currentTicketHolder === 'GA Purchasing Staff'
    ) {
      const payload = {
        requests: purchaseOrderData.map((value) => ({
          purchaseRequest: {
            retainAttachmentIds: value.purchaseRequest.attachments.map(
              (attachment) => attachment.id,
            ),
            purchaseRequestNumber: value.purchaseRequest.purchaseRequestNumber,
          },
          purchaseOrderDtos: value.purchaseOrderDtos.map(
            (purchaseOrderDto) => ({
              purchaseOrderNumber: purchaseOrderDto.purchaseOrderNumber,
              retainAttachmentIds: purchaseOrderDto.attachments.map(
                (attachemnt) => attachemnt.id,
              ),
            }),
          ),
        })),
        retainPurchaseOrderNumbers: purchaseOrderData.flatMap((value) =>
          value.purchaseOrderDtos.map(
            (purchaseOrderDto) => purchaseOrderDto.purchaseOrderNumber,
          ),
        ),
      }

      const formDataPayload = {
        ticketId: data.id,
        isApprove,
        payload,
      }

      await savePurchaseOrder(formDataPayload)(dispatch)
      if (isApprove) {
        onClose()
      } else {
        mutatePurchaseOrder()
      }
    }
    mutateForProcessing()
    } finally {
      isApprove ? setIsSubmitting(false) : setIsSaving(false)
    }
  }

  const handleRecall = async () => {
    setIsRecalling(true)
    try {
    await recallTicket(data.id)(dispatch)
    mutateForProcessing()
    onClose()
    } finally {
      setIsRecalling(false)
    }
  }

  const handleOnHoldTicket = async (
    ticketId: number | string,
    note: string,
  ) => {
    setIsOnHolding(true)
    try {
    await onHoldTicket(ticketId, note)(dispatch)
    mutateForProcessing()
    handleCloseOnHold()
    onClose()
    } finally {
      setIsOnHolding(false)
    }
  }

  const handleDisabledButton = () => {
    if (
      data.currentLayerId === 4 &&
      data.currentTicketHolder === 'GA Purchasing Staff'
    ) {
      return handleValidateQuotationData(quotationData)
    } else if (
      (data.workFlowId == 3 &&
        data.currentLayerId === 14 &&
        data.currentTicketHolder === 'DND Purchasing Admin') ||
      (data.workFlowId == 4 &&
        data.currentLayerId === 15 &&
        data.currentTicketHolder === 'DND Purchasing Admin')
    ) {
      return handleValidatePurchaseRequestData(purchaseRequestData)
    } else if (
      (data.workFlowId == 3 &&
        data.currentLayerId === 15 &&
        data.currentTicketHolder === 'GA Purchasing Staff') ||
      (data.workFlowId == 4 &&
        data.currentLayerId === 16 &&
        data.currentTicketHolder === 'GA Purchasing Staff')
    ) {
      return handleValidatePurchaseOrderData(purchaseOrderData)
    }
  }

  const handleDisableSubmitButton = () => {
    if (
      data.currentLayerId === 4 &&
      data.currentTicketHolder === 'GA Purchasing Staff'
    ) {
      return (
        handleValidateIsAwardedExist(quotationData) ||
        handleValidateQuotationData(quotationData)
      )
    } else if (
      (data.workFlowId == 3 &&
        data.currentLayerId === 14 &&
        data.currentTicketHolder === 'DND Purchasing Admin') ||
      (data.workFlowId == 4 &&
        data.currentLayerId === 15 &&
        data.currentTicketHolder === 'DND Purchasing Admin')
    ) {
      return handleValidatePurchaseRequestData(purchaseRequestData)
    } else if (
      (data.workFlowId == 3 &&
        data.currentLayerId === 15 &&
        data.currentTicketHolder === 'GA Purchasing Staff') ||
      (data.workFlowId == 4 &&
        data.currentLayerId === 16 &&
        data.currentTicketHolder === 'GA Purchasing Staff')
    ) {
      return handleValidatePurchaseOrderData(purchaseOrderData)
    }
  }

  const shouldShow = () => {
    if (
      data.workFlowId === 1 &&
      data.currentLayerId >= 2 &&
      data.currentLayerId <= 11
    ) {
      return true
    } else if (data.workFlowId === 2) {
      return true
    } else if (
      (data.workFlowId === 3 || data.workFlowId === 4) &&
      data.currentLayerId <= 3 &&
      data.currentLayerId >= 2
    ) {
      return true
    } else if (data.workFlowId === 5 && data.currentLayerId >= 4) {
      return true
    }
    return false
  }

  const handleGeneratePdfById = async (id: number) => {
    setIsGeneratingPdf(true)
    try {
    await generatePdfById(id)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  return (
    <React.Fragment>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth  scroll="paper">
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ px: '2rem', pt: '1.5rem' }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{
                  fontFamily: 'Gabarito',
                  fontWeight: 500,
                  fontSize: '22px',
                  color: '#505470',
                }}
              >
                {title ?? 'Default Title'}
              </Typography>
              <IconButton
                size="small"
                aria-label="close"
                onClick={onClose}
                sx={{
                  color: '#505470',
                  width: 20,
                  height: 20,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ my: 2, borderWidth: 2, color: '#B1B8BF' }} />
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            marginTop: 0,
            px: 5,
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {data.status === 'CLOSED' && (
            <ActionButton
              variant="contained"
              bgcolor="#495AFF"
              disabled={isGeneratingPdf}
              onClick={() => handleGeneratePdfById(data.id)}
              sx={{
                width: {
                  xs: '100%', // full width on small screens
                  sm: '20%',
                },
                minWidth: 160,
                px: 2.5,
                whiteSpace: 'nowrap',
                mb: '20px',
                opacity: isGeneratingPdf ? 0.6 : 1,
              }}
            >
              {isGeneratingPdf ? 'Generating...' : 'Generate Summary'}
            </ActionButton>
          )}
          {
            <Box display="flex" justifyContent="center" marginBottom={2}>
              {(data.workFlowId === 3 || data.workFlowId === 4) && (
                <Stepper
                  activeStep={activeStep - 1}
                  alternativeLabel
                  sx={{ width: 700 }}
                >
                  <Step key={'Review'}>
                    <StyledStepLabel
                      isHighlighted={handleIsNextAvailableWithRole(
                        data.workFlowId,
                        data.currentLayerId,
                        1,
                        data.currentTicketHolder,
                        role,
                        canAct,
                      )}
                      onClick={() => {
                        setActiveStep(1)
                      }}
                    >
                      Review
                    </StyledStepLabel>
                  </Step>
                  <Step key={'Quotations'}>
                    <StyledStepLabel
                      isHighlighted={handleIsNextAvailableWithRole(
                        data.workFlowId,
                        data.currentLayerId,
                        4,
                        data.currentTicketHolder,
                        role,
                        canAct,
                      )}
                      onClick={() => {
                        handleIsNextAvailableWithRole(
                          data.workFlowId,
                          data.currentLayerId,
                          4,
                          data.currentTicketHolder,
                          role,
                          canAct,
                        )
                          ? setActiveStep(2)
                          : null
                      }}
                    >
                      Quotations
                    </StyledStepLabel>
                  </Step>
                  <Step key={'Purchase Order'}>
                    <StyledStepLabel
                      isHighlighted={handleIsStepAvailable(
                        data.workFlowId,
                        data.currentLayerId,
                        14,
                        canAct,
                      )}
                      onClick={() => {
                        handleIsStepAvailable(
                          data.workFlowId,
                          data.currentLayerId,
                          14,
                          canAct,
                        )
                          ? setActiveStep(3)
                          : null
                      }}
                    >
                      Purchase Order
                    </StyledStepLabel>
                  </Step>
                </Stepper>
              )}
            </Box>
          }
          <Box sx={{ flex: 1 }}>
            {activeStep === 1 ? (
              <TicketData data={data} />
            ) : activeStep === 2 ? (
              <QuotationForm
                quotationData={quotationData}
                setQuotationData={setQuotationData}
                ticketId={data.id}
                disabled={!canAct || data.currentLayerId > 4}
              />
            ) : activeStep === 3 &&
              ((data.workFlowId == 3 && data.currentLayerId >= 15) ||
                (data.workFlowId == 4 && data.currentLayerId >= 16)) ? (
              <PurchaseOrderForm
                purchaseOrderData={purchaseOrderData}
                setPurchaseOrderData={setPurchaseOrderData}
                disabled={
                  (data.workFlowId == 3 && data.currentLayerId > 15) ||
                  (data.workFlowId == 4 && data.currentLayerId > 16) ||
                  !canAct
                }
                ticketId={data.id}
              />
            ) : (
              <PurchaseRequestForm
                purchaseRequestData={purchaseRequestData}
                setPurchaseRequestData={setPurchaseRequestData}
                ticketId={data.id}
                disabled={!canAct}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {activeStep === 1 && (
            <Grid2 container margin={2} width="100%">
              {shouldShow() && canAct && (
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column-reverse', md: 'row' }}
                  justifyContent="space-between"
                  gap={2}
                  width="100%"
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                      borderColor: 'black',
                      color: 'black',
                      width: { xs: '100%', md: 'auto' },
                      '&:hover': {
                        borderColor: 'black',
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    Close
                  </Button>

                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column-reverse', md: 'row' }}
                    gap={1}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                  >
                    <ActionButton
                      variant="contained"
                      bgcolor="#FF2D2D"
                      disabled={isDenying}
                      onClick={() => handleShowDeny(data)}
                      sx={{ width: { xs: '100%', md: 'auto' }, opacity: isDenying ? 0.6 : 1 }}
                    >
                      {isDenying ? 'Rejecting...' : 'Reject'}
                    </ActionButton>
                    <ActionButton
                      variant="contained"
                      bgcolor="#379F86"
                      sx={{ width: { xs: '100%', md: 'auto' }, opacity: isApproving ? 0.6 : 1 }}
                      disabled={isApproving}
                      onClick={() => {
                        if (
                          (data.currentLayerId === 4 &&
                            role === 'Maintenance Admin' &&
                            data.workFlowId === 5) ||
                          (data.currentLayerId === 8 &&
                            role === 'Maintenance Admin' &&
                            data.workFlowId === 4)
                        ) {
                          handleShowApproveDIS(data)
                        } else if (
                          data.currentLayerId === 10 &&
                          data.workFlowId === 5 &&
                          role === 'Maintenance Admin'
                        ) {
                          handleShowApproveDISWithGatePass(data)
                        } else {
                          handleShowApprove(data)
                        }
                      }}
                    >
                      {isApproving ? 'Approving...' : 'Approve'}
                    </ActionButton>
                  </Box>
                </Box>
              )}
              {handleIsNextAvailableWithRole(
                data.workFlowId,
                data.currentLayerId,
                4,
                data.currentTicketHolder,
                role,
                canAct,
              ) && (
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column-reverse', md: 'row' }}
                  justifyContent="space-between"
                  gap={1}
                  width="100%"
                  mt={2}
                >
                  <Button
                    onClick={onClose}
                    color="inherit"
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => setActiveStep(2)}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Grid2>
          )}

          {/* Step 2 */}
          {activeStep === 2 && (
            <Grid2
              container
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              margin={2}
              sx={{
                flexDirection: { xs: 'column-reverse', md: 'row-reverse' },
                width: '100%',
              }}
            >
              {data.currentLayerId === 4 && canAct ? (
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap={1}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                  <ActionButton
                    variant="contained"
                    onClick={() => handleSave(false)}
                    bgcolor="#fff"
                    sx={{
                      color: '#495AFF',
                      width: { xs: '100%', md: 'auto' },
                      opacity: isSaving ? 0.6 : 1,
                    }}
                    disabled={handleDisabledButton() || isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </ActionButton>
                  <ActionButton
                    variant="contained"
                    bgcolor=""
                    onClick={() => handleSave(true)}
                    disabled={handleDisableSubmitButton() || isSubmitting}
                    sx={{ width: { xs: '100%', md: 'auto' }, opacity: isSubmitting ? 0.6 : 1 }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </ActionButton>
                </Box>
              ) : (((data.workFlowId === 3 && data.currentLayerId >= 14) ||
                  (data.workFlowId === 4 && data.currentLayerId >= 15)) &&
                  canAct) ||
                (((data.workFlowId === 3 && data.currentLayerId > 14) ||
                  (data.workFlowId === 4 && data.currentLayerId > 15)) &&
                  !canAct) ? (
                <Button
                  onClick={() => setActiveStep(3)}
                  color="inherit"
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                  Next
                </Button>
              ) : ((data.workFlowId === 3 &&
                  data.currentLayerId >= 4 &&
                  data.currentLayerId <= 13) ||
                  (data.workFlowId === 4 &&
                    data.currentLayerId >= 4 &&
                    data.currentLayerId <= 14)) &&
                canAct ? (
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap={1}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                  {data.currentLayerId === 7 &&
                    (data.workFlowId === 3 || data.workFlowId === 4) && (
                      <ActionButton
                        variant="contained"
                        onClick={() => handleShowOnHold(data)}
                        bgcolor="#fff"
                        sx={{
                          color: '#495AFF',
                          width: { xs: '100%', md: 'auto' },
                          opacity: isOnHolding ? 0.6 : 1,
                        }}
                        disabled={data.status === 'ON_HOLD' || isOnHolding}
                      >
                        {isOnHolding ? 'Holding...' : 'On Hold'}
                      </ActionButton>
                    )}
                  <ActionButton
                    variant="contained"
                    bgcolor="#FF2D2D"
                    onClick={() => handleShowDeny(data)}
                    sx={{ width: { xs: '100%', md: 'auto' }, opacity: isDenying ? 0.6 : 1 }}
                  >
                    {isDenying ? 'Rejecting...' : 'Reject'}
                  </ActionButton>
                  <ActionButton
                    variant="contained"
                    bgcolor="#379F86"
                    onClick={() => handleShowApprove(data)}
                    sx={{ width: { xs: '100%', md: 'auto' }, opacity: isApproving ? 0.6 : 1 }}
                  >
                    {isApproving ? 'Approving...' : 'Approve'}
                  </ActionButton>
                </Box>
              ) : null}
              <Button
                onClick={() => setActiveStep(1)}
                color="inherit"
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                Back
              </Button>
            </Grid2>
          )}

          {/* Step 3 */}
          {activeStep === 3 && (
            <Grid2
              container
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              margin={2}
              sx={{
                flexDirection: { xs: 'column', md: 'row-reverse' },
                width: '100%',
              }}
            >
              {((data.workFlowId === 3 &&
                data.currentLayerId >= 14 &&
                data.currentLayerId <= 15) ||
                (data.workFlowId === 4 &&
                  data.currentLayerId >= 15 &&
                  data.currentLayerId <= 16)) &&
              canAct ? (
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap={1}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                  <ActionButton
                    variant="contained"
                    onClick={() => handleSave(false)}
                    bgcolor="#fff"
                    sx={{
                      color: '#495AFF',
                      width: { xs: '100%', md: 'auto' },
                      opacity: isSaving ? 0.6 : 1,
                    }}
                    disabled={handleDisabledButton() || isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </ActionButton>
                  <ActionButton
                    variant="contained"
                    bgcolor=""
                    onClick={() => handleSave(true)}
                    disabled={handleDisableSubmitButton() || isSubmitting}
                    sx={{ width: { xs: '100%', md: 'auto' }, opacity: isSubmitting ? 0.6 : 1 }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </ActionButton>
                </Box>
              ) : ((data.workFlowId === 3 &&
                  (data.currentLayerId - 1 === 14 ||
                    data.currentLayerId - 1 === 15)) ||
                  (data.workFlowId === 4 &&
                    (data.currentLayerId - 1 === 15 ||
                      data.currentLayerId - 1 === 16))) &&
                !canAct ? (
                <Button
                  variant="outlined"
                  disabled={isRecalling}
                  onClick={handleRecall}
                  sx={{
                    bgcolor: '#fff',
                    color: '#495AFF',
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  {isRecalling ? 'Recalling...' : 'Recall'}
                </Button>
              ) : ((data.workFlowId === 3 && data.currentLayerId >= 16) ||
                  (data.workFlowId === 4 && data.currentLayerId >= 17)) &&
                canAct ? (
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap={1}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                  {((data.workFlowId === 3 && data.currentLayerId === 16) ||
                    (data.workFlowId === 4 && data.currentLayerId === 17)) && (
                    <ActionButton
                      variant="contained"
                      onClick={() => handleShowOnHold(data)}
                      bgcolor="#fff"
                      sx={{
                        color: '#495AFF',
                        width: { xs: '100%', md: 'auto' },
                        opacity: isOnHolding ? 0.6 : 1,
                      }}
                      disabled={data.status === 'ON_HOLD' || isOnHolding}
                    >
                      {isOnHolding ? 'Holding...' : 'On Hold'}
                    </ActionButton>
                  )}
                  {(data.workFlowId === 3 && data.currentLayerId <= 15) ||
                  (data.workFlowId === 4 && data.currentLayerId <= 16) ? (
                    <ActionButton
                      variant="contained"
                      bgcolor="#FF2D2D"
                      disabled={isDenying}
                      onClick={() => handleShowDeny(data)}
                      sx={{ width: { xs: '100%', md: 'auto' }, opacity: isDenying ? 0.6 : 1 }}
                    >
                      {isDenying ? 'Rejecting...' : 'Reject'}
                    </ActionButton>
                  ) : null}
                  <ActionButton
                    variant="contained"
                    bgcolor="#379F86"
                    onClick={() => handleShowApprove(data)}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                  >
                    {(data.workFlowId === 3 &&
                      (data.currentLayerId === 16 ||
                        data.currentLayerId === 17)) ||
                    (data.workFlowId === 4 &&
                      (data.currentLayerId === 17 ||
                        data.currentLayerId === 18))
                      ? 'Approve'
                      : 'Submit'}
                  </ActionButton>
                </Box>
              ) : null}
              <Button
                onClick={() => setActiveStep(2)}
                color="inherit"
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                Back
              </Button>
            </Grid2>
          )}
        </DialogActions>
        {showApprove && (
          <ApproveModal
            data={approveData}
            open={showApprove}
            onClose={handleCloseApprove}
            onApprove={handleApproveTicket}
            mutate={mutateForProcessing}
            onCloseViewTicket={onClose}
            isNested
          />
        )}

        {showApproveDIS && (
          <ApproveDISModal
            data={approveDISData}
            open={showApproveDIS}
            onClose={handleCloseApproveDIS}
            onApprove={handleApproveTicket}
            isNested
          />
        )}
        {showApproveDISWithGatePass && (
          <ApproveDISWithGatePassModal
            data={approveDISWithGatePassData}
            open={showApproveDISWithGatePass}
            onClose={handleCloseApproveDISWithGatePass}
            onApprove={handleApproveTicket}
            onCloseViewTicket={onClose}
            mutate={mutateForProcessing}
            isNested
          />
        )}

        {showOnHold && (
          <OnHoldTicketModal
            data={onHoldData}
            open={showOnHold}
            onClose={handleCloseOnHold}
            onHold={handleOnHoldTicket}
            title="On Hold Ticket"
          />
        )}
        {showDeny && (
          <DenyModal
            data={denyData}
            open={showDeny}
            onClose={handleCloseDeny}
            onDeny={handleDenyTicket}
            onRevise={handleReviseTicket}
            mutate={mutateForProcessing}
            isNested
          />
        )}
      </Dialog>
    </React.Fragment>
  )
}

export default ViewTicketModal
